import { z } from 'zod';
import { ALERT_CRITICALITIES } from './alerts';
import { RISK_LEVELS } from './initiatives';
import { MEETING_TYPES } from './meetings';
import { TRAFFIC_LIGHTS, type TrafficLight } from './status';
import type { KpiDirectionName } from './kpis';
import type { TrafficRule } from './performance';

/**
 * Visão Geral da Empresa (T-140, RF-09; DEC-054): a home com a performance da empresa, as perspectivas, os cards e o
 * bloco "Requer atenção", lida dos agregados pré-calculados.
 */

/** Itens de cada grupo do "Requer atenção" (os mais graves). */
export const OVERVIEW_ATTENTION_LIMIT = 5;

/** Mês de referência: os dois juntos, ou nenhum (o último mês com dado). */
export const overviewQuerySchema = z
  .object({
    year: z.coerce.number().int().min(2000).max(2100).optional(),
    month: z.coerce.number().int().min(1).max(12).optional(),
  })
  .refine((v) => (v.year === undefined) === (v.month === undefined), {
    message: 'Informe o ano e o mês juntos.',
    path: ['month'],
  });
export type OverviewQuery = z.input<typeof overviewQuerySchema>;

const light = z.enum(TRAFFIC_LIGHTS);

/** Grupo do "Requer atenção": o total e os primeiros itens; null = sem acesso ao módulo de origem. */
const group = <T extends z.ZodTypeAny>(item: T) =>
  z.object({ total: z.number().int(), items: z.array(item) }).nullable();

export const overviewSchema = z.object({
  project: z.object({
    id: z.string().uuid(),
    name: z.string(),
    horizonStart: z.number().int(),
    horizonEnd: z.number().int(),
  }),
  year: z.number().int(),
  month: z.number().int(),
  /** Quando os agregados foram calculados pela última vez (null: nada calculado no mês). */
  computedAt: z.string().nullable(),
  /** Performance da empresa no mês e a diferença (pontos percentuais) para o mês anterior. */
  company: z.object({ performance: z.number().nullable(), light, trend: z.number().nullable() }).nullable(),
  perspectives: z.array(
    z.object({
      id: z.string().uuid(),
      name: z.string(),
      performance: z.number().nullable(),
      light,
      trend: z.number().nullable(),
    }),
  ),
  /** Cards (null = sem acesso ao módulo de origem). */
  cards: z.object({
    objectives: z.object({ total: z.number().int(), critical: z.number().int() }).nullable(),
    kpis: z.object({ active: z.number().int(), offTarget: z.number().int() }).nullable(),
    initiatives: z.object({ open: z.number().int() }).nullable(),
    actions: z.object({ late: z.number().int() }).nullable(),
  }),
  attention: z.object({
    criticalObjectives: group(
      z.object({
        id: z.string().uuid(),
        name: z.string(),
        perspective: z.string(),
        performance: z.number().nullable(),
        light,
      }),
    ),
    /** Indicadores críticos (PRD §5.4): KPIs vermelhos no mês, pior performance primeiro. */
    criticalKpis: group(
      z.object({
        kpiId: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        performance: z.number().nullable(),
      }),
    ),
    fallingKpis: group(
      z.object({
        kpiId: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        alertId: z.string().uuid(),
        criticality: z.enum(ALERT_CRITICALITIES),
        message: z.string(),
      }),
    ),
    lateActions: group(
      z.object({
        id: z.string().uuid(),
        title: z.string(),
        dueDate: z.string(),
        planId: z.string().uuid(),
        planName: z.string(),
        responsibleName: z.string().nullable(),
      }),
    ),
    nearLimitTargets: group(
      z.object({
        kpiId: z.string().uuid(),
        code: z.string(),
        name: z.string(),
        performance: z.number(),
        /** Onde começa o vermelho (o mínimo do amarelo da regra do KPI). */
        redBelow: z.number(),
      }),
    ),
    pendingMeetings: group(
      z.object({
        id: z.string().uuid(),
        title: z.string(),
        type: z.enum(MEETING_TYPES),
        startsAt: z.string(),
      }),
    ),
    untreatedRisks: group(
      z.object({
        id: z.string().uuid(),
        description: z.string(),
        probability: z.enum(RISK_LEVELS),
        impact: z.enum(RISK_LEVELS),
        initiative: z.object({ id: z.string().uuid(), name: z.string() }),
      }),
    ),
    pendingDecisions: group(
      z.object({
        id: z.string().uuid(),
        text: z.string(),
        dueDate: z.string().nullable(),
        responsibleName: z.string().nullable(),
        meeting: z.object({ id: z.string().uuid(), title: z.string() }),
        /** Atrasada: prazo vencido. */
        late: z.boolean(),
      }),
    ),
  }),
});
export type Overview = z.infer<typeof overviewSchema>;

/**
 * Meta próxima do limite (DEC-054): KPI amarelo na metade de baixo da faixa amarela — abaixo do meio entre o mínimo do
 * amarelo e o mínimo do verde (na faixa ideal, o teto do amarelo é 100%).
 */
export function nearRedLimit(
  performance: number | null,
  lightValue: TrafficLight,
  rule: TrafficRule,
  direction: KpiDirectionName,
): boolean {
  if (performance === null || lightValue !== 'yellow') return false;
  const top = direction === 'RANGE' ? 100 : rule.greenMin;
  return performance < (rule.yellowMin + top) / 2;
}

/** Diferença em pontos percentuais (uma casa), ou null sem os dois valores. */
export function performanceTrend(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null;
  return Math.round((current - previous) * 10) / 10;
}

/** Mês anterior (dezembro do ano anterior para janeiro). */
export const previousMonth = (year: number, month: number) =>
  month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
